import { Button, H3, H4, ITreeNode, Menu, Tree } from "@blueprintjs/core";
import "@blueprintjs/core/lib/css/blueprint.css";
import { Businesses, Routes } from "@just-in-types";
import * as React from "react";
import { connect } from "react-redux";
import BusinessAdder from "../components/BusinessAdder";
import BusinessTree from "../components/BusinessTree";
import SiteTree from "../components/SiteTree";
import { RoutesObjectDisabled } from "../components/SiteTree.d";
import { IState } from "../store";
import {
  GetArchiveBusinessesSnapshot,
  getArchiveBusinessesSnapshot,
  GetBusinessesSnapshot,
  getBusinessesSnapshot,
} from "../store/actions";

export interface IBusinessNode extends ITreeNode {
  childNodes: ISiteNode[];
  nodeData: { component: JSX.Element };
}
export interface ISiteNode extends ITreeNode {
  nodeData: {
    businessId: string;
    qrUrl: string;
    url: string;
    component: JSX.Element;
  };
}

interface IBusinessesDispatchProps {
  onGetBusinessesSnapshot: GetBusinessesSnapshot;
}

interface IArchiveBusinessesDispatchProps {
  onGetArchiveBusinessesSnapshot: GetArchiveBusinessesSnapshot;
}

interface IBusinessesStateProps {
  archivebusinesses: Businesses.Business[];
  businesses: Businesses.Business[];
  unsubscribe: () => void;
}

type Props = IBusinessesDispatchProps &
  IArchiveBusinessesDispatchProps &
  IBusinessesStateProps;

interface IBusinessesState {
  activeNode: null | IBusinessNode | ISiteNode;
  archivetree: Array<ITreeNode<any>>;
  showBusinessAdder: boolean;
  tree: Array<ITreeNode<any>>;
}

export const convertToPath = (name: string) =>
  name
    .replace("&", "and")
    .replace(/[^\w-\s]/g, "")
    .replace(/\s/g, "-")
    .replace(/-{2,}/g, "-")
    .toLowerCase();

export const emptySite: {
  disabled: RoutesObjectDisabled;
  routes: Routes.RoutesObject;
} = {
  disabled: {
    covidquestions: { alwaysShow: false, enabled: false, optional: false },
    customertype: { alwaysShow: false, enabled: false, optional: false },
    dateofbirth: { alwaysShow: false, enabled: false, optional: false },
    email: { alwaysShow: false, enabled: false, optional: false },
    language: { alwaysShow: false, enabled: false, optional: false },
    name: { alwaysShow: false, enabled: false, optional: false },
    signature: {
      alwaysShow: false,
      enabled: false,
      optional: false,
      takeMugshot: false,
      waiverText: false,
      waiverTextEs: false,
    },
    supercard: { alwaysShow: false, enabled: false, optional: false },
    vehicles: { alwaysShow: false, enabled: false, optional: false },
    zip: { alwaysShow: false, enabled: false, optional: false },
  },
  routes: {
    covidquestions: { alwaysShow: false, enabled: false, optional: false },
    customertype: { alwaysShow: false, enabled: false, optional: false },
    dateofbirth: { alwaysShow: false, enabled: false, optional: false },
    email: { alwaysShow: false, enabled: false, optional: false },
    language: { alwaysShow: false, enabled: false, optional: false },
    name: { alwaysShow: false, enabled: false, optional: false },
    optin: { alwaysShow: false, enabled: false, optional: false },
    signature: {
      alwaysShow: false,
      enabled: false,
      optional: false,
      takeMugshot: false,
      waiverText: "",
    },
    supercard: { alwaysShow: false, enabled: false, optional: false },
    vehicles: { alwaysShow: false, enabled: false, optional: false },
    zip: { alwaysShow: false, enabled: false, optional: false },
  },
};

class Businesses extends React.Component<Props, IBusinessesState> {
  public state: IBusinessesState = {
    activeNode: null,
    archivetree: [],
    showBusinessAdder: false,
    tree: [],
  };

  public componentDidMount() {
    this.buildTree();
    this.props.onGetBusinessesSnapshot();
    this.props.onGetArchiveBusinessesSnapshot();
  }

  public componentDidUpdate({ businesses: prevBusinesses }: Props) {
    const { businesses } = this.props;

    if (prevBusinesses.length !== businesses.length) {
      this.buildTree();
    }
  }

  public componentWillUnmount() {
    this.props.unsubscribe();
  }

  public render() {
    const { activeNode, archivetree, showBusinessAdder, tree } = this.state;
    const activeComponent =
      activeNode && activeNode.nodeData && activeNode.nodeData.component;

    return (
      <div
        style={{
          display: "flex",
          maxWidth: "95vw",
        }}
      >
        <div
          style={{
            padding: "1rem",
            width: 300,
          }}
        >
          <Menu style={{ width: "100%" }}>
            <Button
              fill={true}
              icon="add"
              intent="success"
              onClick={this.toggleBusinessAdder}
            >
              add new business
            </Button>
            <BusinessAdder
              onToggleShow={this.toggleBusinessAdder}
              show={showBusinessAdder}
            />
            <Menu.Divider />
            <div>
              <Tree
                contents={tree}
                onNodeClick={this.handleNodeClick}
                onNodeCollapse={this.handleNodeCollapse}
                onNodeExpand={this.handleNodeExpand}
              />
            </div>

            <H4>Archive businesses</H4>
            <Menu.Divider />
            <div>
              <Tree
                contents={archivetree}
                onNodeClick={this.handleNodeClick}
                onNodeCollapse={this.handleNodeCollapse}
                onNodeExpand={this.handleNodeExpand}
              />
            </div>
          </Menu>
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            padding: "1rem 1rem 1rem 300px",
          }}
          id="active-comp-style"
        >
          {activeComponent || (
            <H3>Use the menu on the left to choose a business.</H3>
          )}
        </div>
      </div>
    );
  }

  private buildTree = () => {
    const { businesses, archivebusinesses } = this.props;

    const tree = businesses.map((business, index) => {
      const siteTrees = business.sites.map((site) =>
        this.buildSiteTree(site, business.businessId, business.path)
      );

      return this.buildBusinessTree(business, index, siteTrees);
    });

    this.setState({ tree });

    const archivetree = archivebusinesses.map((business, index) => {
      const siteTrees = business.sites.map((site) =>
        this.buildSiteTree(site, business.businessId, business.path)
      );

      return this.buildBusinessTree(business, index, siteTrees);
    });

    this.setState({ archivetree });
  };

  private buildBusinessTree = (
    business: Businesses.Business,
    index: number,
    siteNodes: ISiteNode[]
  ): IBusinessNode => {
    const { businessId, path } = business;
    const node: IBusinessNode = {
      childNodes: siteNodes,
      hasCaret: false,
      icon: "office",
      id: businessId,
      isSelected: false,
      label: path,
      nodeData: {
        component: <BusinessTree business={business} siteNodes={siteNodes} />,
      },
    };

    if (index === 0) {
      node.isSelected = true;

      this.setState({ activeNode: node });
    }

    return node;
  };

  private buildSiteTree = (
    site: Businesses.Site,
    businessId: string,
    businessPath: string
  ): ISiteNode => {
    const { path, siteId, isArchive } = site;
    const url = `https://kiosk.texnrewards.com/${businessPath}/${path}`;
    const qrUrl = `https://just-in.texnrewards.com/just-in/${businessPath}/${path}`;

    let labelName = "";
    if (isArchive === "1") {
      labelName = `${path} - Archived`;
    } else {
      labelName = path;
    }

    return {
      icon: "desktop",
      id: siteId,
      label: labelName,
      nodeData: {
        businessId,
        component: (
          <SiteTree
            businessId={businessId}
            businessPath={businessPath}
            qrUrl={qrUrl}
            siteId={siteId}
            url={url}
          />
        ),
        qrUrl,
        url,
      },
    };
  };

  private handleNodeClick = (
    nodeData: IBusinessNode | ISiteNode,
    nodePath: number[],
    e: React.MouseEvent<HTMLElement>
  ) => {
    const originallySelected = nodeData.isSelected;

    if (!e.shiftKey) {
      this.forEachNode(this.state.tree, (node) => (node.isSelected = false));
    }

    this.handleNodeExpand(nodeData, nodePath);

    nodeData.isSelected =
      originallySelected === null ? true : !originallySelected;

    this.setState({ ...this.state, activeNode: nodeData });
  };

  private handleNodeCollapse = (nodeData: ITreeNode) => {
    nodeData.isExpanded = false;
    this.setState(this.state);
  };

  private handleNodeExpand = (nodeData: ITreeNode, nodePath: number[]) => {
    const { tree } = this.state;
    let parentNode: null | ITreeNode<any> = null;

    if (nodePath.length > 1) {
      const parentPath = nodePath[0];
      parentNode = tree[parentPath];

      parentNode.isExpanded = true;
    }

    nodeData.isExpanded = true;

    this.forEachNode(
      this.state.tree,
      this.handleNodeCollapse,
      parentNode || nodeData
    );

    this.setState(this.state);
  };

  private forEachNode(
    nodes: undefined | Array<ITreeNode<any>>,
    callback: (node: ITreeNode<any>) => void,
    nodeToSkip?: ITreeNode<any>
  ) {
    if (!nodes) {
      return;
    }

    for (const node of nodes) {
      if (node && !(nodeToSkip && node.id === nodeToSkip.id)) {
        callback(node);

        this.forEachNode(node.childNodes, callback);
      }
    }
  }

  private toggleBusinessAdder = () =>
    this.setState(({ showBusinessAdder }) => ({
      showBusinessAdder: !showBusinessAdder,
    }));
}

const mapStateToProps = ({
  businesses: { businesses, unsubscribeBusinesses, archivebusinesses },
}: IState) => ({
  archivebusinesses,
  businesses,
  unsubscribe: unsubscribeBusinesses,
});

const mapDispatchToProps = {
  onGetArchiveBusinessesSnapshot: getArchiveBusinessesSnapshot,
  onGetBusinessesSnapshot: getBusinessesSnapshot,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(Businesses);



// WEBPACK FOOTER //
// ./src/views/Businesses.tsx